class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

/** CMS HeaderMegaMenu parity: desktop hover-open with delayed close. */
const HEADER_MENU_CLOSE_DELAY_MS = 175;

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.closeTimer = null;
    this.isDesktop = false;
    this.desktopMedia = window.matchMedia('(min-width: 990px)');
    this.onDesktopMediaChange = this.syncDesktop.bind(this);
    this.onDetailsMouseEnter = this.onDetailsMouseEnter.bind(this);
    this.onDetailsMouseLeave = this.onDetailsMouseLeave.bind(this);
    this.onContentMouseEnter = this.onContentMouseEnter.bind(this);
    this.onContentMouseLeave = this.onContentMouseLeave.bind(this);

    this.syncDesktop();
    if (this.desktopMedia.addEventListener) {
      this.desktopMedia.addEventListener('change', this.onDesktopMediaChange);
    } else {
      this.desktopMedia.addListener(this.onDesktopMediaChange);
    }

    this.mainDetailsToggle.addEventListener('mouseenter', this.onDetailsMouseEnter);
    this.mainDetailsToggle.addEventListener('mouseleave', this.onDetailsMouseLeave);

    if (this.content) {
      this.content.addEventListener('mouseenter', this.onContentMouseEnter);
      this.content.addEventListener('mouseleave', this.onContentMouseLeave);
    }
  }

  disconnectedCallback() {
    this.cancelScheduledClose();
    if (this.desktopMedia.removeEventListener) {
      this.desktopMedia.removeEventListener('change', this.onDesktopMediaChange);
    } else if (this.desktopMedia.removeListener) {
      this.desktopMedia.removeListener(this.onDesktopMediaChange);
    }
    this.mainDetailsToggle.removeEventListener('mouseenter', this.onDetailsMouseEnter);
    this.mainDetailsToggle.removeEventListener('mouseleave', this.onDetailsMouseLeave);
    if (this.content) {
      this.content.removeEventListener('mouseenter', this.onContentMouseEnter);
      this.content.removeEventListener('mouseleave', this.onContentMouseLeave);
    }
  }

  syncDesktop() {
    this.isDesktop = this.desktopMedia.matches;
    if (!this.isDesktop) this.cancelScheduledClose();
  }

  setExpanded(open) {
    this.mainDetailsToggle.querySelector('summary')?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  cancelScheduledClose() {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  scheduleClose() {
    if (!this.isDesktop) return;
    this.cancelScheduledClose();
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      this.close();
    }, HEADER_MENU_CLOSE_DELAY_MS);
  }

  closeSiblings() {
    const nav = this.closest('.header__inline-menu');
    if (!nav) return;
    nav.querySelectorAll('details[open]').forEach((details) => {
      if (details === this.mainDetailsToggle) return;
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  }

  onDetailsMouseEnter() {
    if (!this.isDesktop) return;
    this.cancelScheduledClose();
    this.closeSiblings();
    this.mainDetailsToggle.setAttribute('open', '');
    this.setExpanded(true);
  }

  onDetailsMouseLeave() {
    this.scheduleClose();
  }

  onContentMouseEnter() {
    if (!this.isDesktop) return;
    this.cancelScheduledClose();
  }

  onContentMouseLeave() {
    this.scheduleClose();
  }

  close() {
    this.cancelScheduledClose();
    super.close();
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (this.mainDetailsToggle.open) {
      this.closeSiblings();
      this.setExpanded(true);
    } else {
      this.setExpanded(false);
    }

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
